import React, { useState, useEffect } from "react";
import BaseTailoringQuestions from "../shared/BaseTailoringQuestions";
import type { TailoringQuestion } from "../shared/BaseTailoringQuestions";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { AlertCircle, FileText, FileSignature } from "lucide-react";
import communicationData from "../../constants/ConcludingProcedures/Communication.json";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "../planning/PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

interface CommunicationWithGovernanceLandingProps {
  onSelect?: (section: string) => void;
}

// Transform JSON structure to TailoringQuestion format
function transformJSONToQuestions(jsonData: any): TailoringQuestion[] {
  const questions: TailoringQuestion[] = [];
  const data = jsonData.communicationWithTCWG;

  // Helper to create a question
  const createQuestion = (
    id: string,
    questionText: string,
    type: "radio" | "text",
    options?: string[],
    followUp?: TailoringQuestion["follow_up"]
  ): TailoringQuestion => {
    return {
      id,
      question: questionText,
      type,
      options,
      follow_up: followUp,
    };
  };

  // CTCWG-1: Communication confirmation
  if (data.communicationConfirmation) {
    const q = data.communicationConfirmation;
    const followUp: TailoringQuestion["follow_up"] = q.documentation
      ? {
          id: "CTCWG-1-doc",
          question: q.documentation.question,
          type: "text",
          condition: "Confirmed",
        }
      : undefined;
    questions.push(
      createQuestion(
        "CTCWG-1",
        q.question,
        "radio",
        [q.option.label],
        followUp
      )
    );
  }

  // CTCWG-2: Two-way communication
  if (data.twoWayCommunication) {
    const q = data.twoWayCommunication;
    const options = q.options?.map((opt: any) => opt.label) || [];
    
    // Find the "Other response" option
    const otherOption = q.options?.find((opt: any) => opt.textbox);
    const followUp: TailoringQuestion["follow_up"] = otherOption
      ? {
          id: "CTCWG-2-other",
          question: "Please provide details:",
          type: "text",
          condition: "Other response",
        }
      : undefined;
    
    questions.push(
      createQuestion("CTCWG-2", q.question, "radio", options, followUp)
    );
  }

  // CTCWG-3: Independence communication (only for listed companies)
  // Note: This will need conditional rendering based on entity type
  // For now, we'll include it but it should be conditionally shown
  if (data.listedCompanyOnly?.independenceCommunication) {
    const q = data.listedCompanyOnly.independenceCommunication;
    questions.push(
      createQuestion(
        "CTCWG-3",
        q.question,
        "radio",
        [q.option.label]
      )
    );
  }

  return questions;
}

const WORKSPACE_NAME = "Communication with those charged with governance";

export default function CommunicationWithGovernanceLanding(_props: CommunicationWithGovernanceLandingProps) {
  const [questions, setQuestions] = useState<TailoringQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffs, setCardSignoffs] = useState<MiscSignoffEntry[]>([]);

  const loadCardSignoffs = React.useCallback(async () => {
    try {
      const data = await loadMiscSignoffs();
      setCardSignoffs(getSignoffEntriesForCard(WORKSPACE_NAME, WORKSPACE_NAME, data.signoff));
    } catch {
      setCardSignoffs([]);
    }
  }, []);

  useEffect(() => {
    loadCardSignoffs();
  }, [loadCardSignoffs]);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use the imported JSON data directly
        const jsonData = communicationData;

        const transformedQuestions = transformJSONToQuestions(jsonData);
        setQuestions(transformedQuestions);
      } catch (err) {
        console.error("Failed to load questions:", err);
        setError(err instanceof Error ? err.message : "Failed to load questions");
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, []);

  if (loading) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Communication with those charged with governance</h2>
          <p className="text-white/60">Loading questions...</p>
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4da3ff] mx-auto mb-4"></div>
              <p>Loading questions from JSON file...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Communication with those charged with governance</h2>
          <p className="text-white/60">Error loading questions</p>
        </div>
        <Card className="border-red-500/20 bg-red-500/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-300">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Communication with those charged with governance</h2>
          <p className="text-white/60">No questions found</p>
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <FileText className="h-8 w-8 mx-auto mb-4 text-white/40" />
              <p>No questions available. Please check the JSON file.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-2 flex-wrap px-6 pt-2">
        <PlanningSignoffTokens signoffs={cardSignoffs} className="mt-0" />
        <Button variant="outline" size="sm" onClick={() => setSignoffDialogOpen(true)} className="border-white/20 text-white hover:bg-white/10">
          <FileSignature className="h-4 w-4 mr-1.5" />
          Sign off
        </Button>
      </div>
      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={[WORKSPACE_NAME]}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />
      <BaseTailoringQuestions
        areaName="CTCWG"
        title="Communication with those charged with governance"
        description="Answer the following questions based on communication with those charged with governance requirements."
        questions={questions}
        onComplete={(answers) => {
          console.log("Communication with Governance answers:", answers);
        }}
      />
    </div>
  );
}
